import moment from 'moment'
import { useState } from 'react'
import { useLocalStorage } from '@gravitate-js/excalibrr'
import type { Dayjs } from 'dayjs'

export interface DayRange {
  back: number
  ahead: number
}

export interface DateFilters {
  From: moment.Moment | Dayjs
  To: moment.Moment | Dayjs
}

export type SetDayRangeValue = DayRange | DateFilters

export interface DateRangeFilterResult {
  filters: DateFilters
  setFilters: (value: DateFilters) => void
  setDayRange: (value: SetDayRangeValue) => void
  dayRangeStorage: DayRange
}

export interface DateRangeFilterOptions {
  sticky?: boolean
}

export interface DateRangeFilterOptions {
  sticky?: boolean
}

export const useDateRangeFilter = (
  storageKey: string,
  defaultRange: DayRange = { back: -3, ahead: 1 },
  options: DateRangeFilterOptions = { sticky: true }
): DateRangeFilterResult => {
  const { sticky = true } = options

  // Only use localStorage if sticky is enabled
  const { value: dayRangeStorage, setValue: setLocalDayRange } = sticky
    ? useLocalStorage<DayRange>(storageKey, defaultRange)
    : { value: null, setValue: () => {} }

  // Determine the initial range based on sticky setting
  const getInitialRange = (): DayRange => {
    if (sticky && dayRangeStorage) {
      return dayRangeStorage
    }
    return defaultRange
  }

  const initialRange = getInitialRange()

  // Initialize filters based on the determined range
  const [filters, setFilters] = useState<DateFilters>({
    From: moment().startOf('day').add(initialRange.back, 'day'),
    To: moment().endOf('day').add(initialRange.ahead, 'day'),
  })

  // Update filters and optionally localStorage based on sticky setting
  const setDayRange = (value: SetDayRangeValue): void => {
    // Check if value is DayRange type
    if ('back' in value && 'ahead' in value) {
      // Update filters
      setFilters({
        From: moment().startOf('day').add(value.back, 'day'),
        To: moment().endOf('day').add(value.ahead, 'day'),
      })

      // Only update localStorage if sticky is enabled
      if (sticky) {
        setLocalDayRange({
          back: value.back,
          ahead: value.ahead,
        })
      }
      return
    }

    // Update filters with DateFilters value
    setFilters({ ...filters, ...value })

    // Calculate and store the range if sticky is enabled
    if (sticky) {
      setLocalDayRange({
        back: moment(value.From).diff(moment().startOf('day'), 'days'),
        ahead: moment(value.To).diff(moment().startOf('day'), 'days'),
      })
    }
  }

  return {
    filters,
    setFilters,
    setDayRange,
    dayRangeStorage: sticky ? dayRangeStorage : null,
  }
}
